import { GLOBAL } from 'saddlebag-browser';
import { v4 as uuidv4 } from 'uuid';

import { clickEventItems, TAB_ID, TRACKING } from './constants';

import type {
  EventTypes,
  GrapplerEvent,
  Vertical,
} from '@web-platform/global-components-utils/tracking';

export type NavigationTrackingComponent = 'global_navigation' | 'search_controls_navigation';

type BehaviourEvents = {
  lastImpressionGuids: Record<Vertical, string>;
  getImpressionEvent: (
    vertical: Vertical,
    componentName?: NavigationTrackingComponent,
  ) => GrapplerEvent | undefined;
  getClickEvent: (
    vertical: Vertical,
    componentName?: NavigationTrackingComponent,
  ) => GrapplerEvent | undefined;
};

/**
 * This is a temporary change and will be removed once MBD SDK for web is released.
 * For more context please refer here: https://skyscanner.atlassian.net/l/cp/sbtHhBnV
 * How view event id is exposed: https://skyscanner.atlassian.net/l/cp/P7R1HdwJ
 * Motivation for this change: https://skyscanner.atlassian.net/l/cp/CPxyVqGm
 * To be removed: behaviourEvents, getImpressionEvent, getClickEvent, event emission in HeaderTab
 * Cleanup ticket: https://skyscanner.atlassian.net/browse/CAPY-233
 */
export const behaviourEvents: BehaviourEvents = {
  lastImpressionGuids: {
    // flight not needed but kept for consistency
    flight: '',
    hotel: '',
    car_hire: '',
    package: '',
  },
  getImpressionEvent: (
    vertical: Vertical,
    componentName: NavigationTrackingComponent = 'global_navigation',
  ): GrapplerEvent | undefined => {
    // eslint-disable-next-line no-underscore-dangle
    const screenViewEventGuid = GLOBAL.getWindow().__internal?.screenViewEventGuid;

    // In Single Page applications, this navigation can render before Trackolding has populated screenViewEventGuid.
    // We cannot emit a valid impression without that GUID, but the user may click a tab after it
    // becomes available. Store an ID now so that click can emit an interaction linked to it.
    // This intentionally permits an interaction without a matching impression: downstream
    // entrypoint analysis identifies it from the component name and vertical fields instead.
    // This is a temporary workaround until a more permanent solution has been implemented.
    behaviourEvents.lastImpressionGuids[vertical] = uuidv4();

    if (!screenViewEventGuid) {
      return undefined;
    }

    return {
      topic: 'grappler-track',
      data: {
        eventName: 'mbd-web',
        fullSchemaName: 'user_behaviour_data_domain.ElementImpression',
        message: {
          view_guid: screenViewEventGuid,
          common: {
            component_name: componentName,
            numeric_properties: {},
            string_properties: {
              vertical,
            },
            boolean_properties: {},
          },
        },
        messageOpts: {
          messageGuid: behaviourEvents.lastImpressionGuids[vertical],
        },
        isMiniEvent: true,
        consentRequired: true,
      },
    };
  },
  getClickEvent: (
    vertical: Vertical,
    componentName: NavigationTrackingComponent = 'global_navigation',
  ): GrapplerEvent | undefined => {
    const impressionGuid = behaviourEvents.lastImpressionGuids[vertical];
    // eslint-disable-next-line no-underscore-dangle
    const screenViewEventGuid = GLOBAL.getWindow().__internal?.screenViewEventGuid;

    if (!impressionGuid || !screenViewEventGuid) {
      return undefined;
    }

    return {
      topic: 'grappler-track',
      data: {
        eventName: 'mbd-web',
        fullSchemaName: 'user_behaviour_data_domain.ElementInteraction',
        message: {
          element_impression_guid: impressionGuid,
          interaction_type: 1,
          common: {
            component_name: componentName,
            numeric_properties: {},
            string_properties: {
              vertical,
              view_guid: screenViewEventGuid,
            },
            boolean_properties: {},
          },
        },
        isMiniEvent: true,
        consentRequired: true,
      },
    };
  },
};

// These values need to map to exactly what is in the website_header.proto in the schemas repo
const mapClickItemToHeaderProtoEnum = (item: string) => {
  const protoValues = [
    'FLIGHTS',
    'HOTELS',
    'CAR_HIRE',
    'PACKAGES',
    'LOGO',
    'CULTURE_SELECTOR',
    'LOGIN',
    'HELP',
    'SEARCH_BOX',
    'SEARCH_BOX_SUBMIT',
    'COVID_INSURANCE_BUTTON',
    'SAVED_LIST',
  ];
  const id = item.toUpperCase().replace(/ /g, '_');
  return protoValues.includes(id) ? id : 'UNSET_HEADER_ITEM';
};

export const clickEvents = (
  item: string,
  eventType: string,
  componentName: NavigationTrackingComponent = 'global_navigation',
): EventTypes[] => {
  const events: EventTypes[] = [
    {
      topic: 'tracking-event',
      data: {
        ga: [
          {
            category: `Header Item ${eventType}`,
            action: item,
          },
        ],
      },
    },
    {
      topic: 'grappler-track',
      data: {
        eventName: `${TRACKING.MESSAGES.CLICK}`,
        fullSchemaName: `${TRACKING.SCHEMA}.${TRACKING.MESSAGES.CLICK}`,
        message: {
          item: mapClickItemToHeaderProtoEnum(item),
        },
        isMiniEvent: true,
        consentRequired: true,
      },
    },
  ];

  if (item in clickEventsToVertical) {
    const vertical = clickEventsToVertical[item];
    const behaviourClickEvent = behaviourEvents.getClickEvent(vertical, componentName);
    if (behaviourClickEvent) events.push(behaviourClickEvent);
  }
  return events;
};

const clickEventsToVertical = {
  [clickEventItems[TAB_ID.HOTELS]]: 'hotel',
  [clickEventItems[TAB_ID.CARHIRE]]: 'car_hire',
  [clickEventItems[TAB_ID.PACKAGES]]: 'package',
} as const;

export const smartMetricEvent = (smartMetricName: string): GrapplerEvent[] => [
  {
    topic: 'grappler-track',
    data: {
      eventName: 'SmartMetric',
      fullSchemaName: 'experiment_smart_metrics.SmartMetric',
      message: {
        name: smartMetricName,
      },
      isMiniEvent: true,
      consentRequired: true,
    },
  },
];
